import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useBusinessContext } from '@/context/BusinessContext';

interface SubscriptionRequiredRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

/**
 * Route guard that requires an active or trialing subscription.
 * Redirects to /select-plan if no valid subscription exists.
 * Also waits for business data to load before rendering children.
 */
const SubscriptionRequiredRoute: React.FC<SubscriptionRequiredRouteProps> = ({
  children,
  fallbackPath = "/select-plan"
}) => {
  const { user, loading: authLoading } = useAuth();
  const { subscription, isLoading: subscriptionLoading, isPaymentsEnabled } = useSubscription();
  const { loadingBusinesses } = useBusinessContext();

  // Return null while loading - GlobalLayout is already showing navbar/sidebar
  // so we don't want to show a full page skeleton inside the content area
  if (authLoading || subscriptionLoading || loadingBusinesses) {
    return null;
  }

  // Not authenticated - redirect to sign in
  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  // If payments are disabled, allow access (development/testing mode)
  if (!isPaymentsEnabled) {
    return <>{children}</>;
  }

  // Check for valid subscription (active or trialing)
  const hasValidSubscription = subscription &&
    ['active', 'trialing'].includes(subscription.status);

  if (!hasValidSubscription) {
    // No valid subscription - redirect to plan selection
    return <Navigate to={fallbackPath} replace />;
  }

  // User has valid subscription, render children
  return <>{children}</>;
};

export default SubscriptionRequiredRoute;
